// Axis tick VALUES for a logarithmic slider (ACT2-569).
//
// On a linear axis the quartiles are the natural ticks — they are evenly spread and they say something
// about the population. On a log axis they are not: p25 and p50 can sit inside the same tenth of the
// track while four empty decades run to the right. What a reader needs there is the magnitude ladder —
// 100, 1k, 10k, 100k — so the axis says WHERE they are, and the landmark labels say who is there.

/**
 * The 1-2-5 ladder used to subdivide a frame too short for decade ticks alone.
 *
 * TUNING. Add 3 for a denser ladder if a two-decade frame still reads as unlabelled; drop to `[1, 5]`
 * if the fine ticks crowd. Do NOT extend past one decade's worth (e.g. adding 10) — the decade loop
 * already emits those and they would double up.
 */
const SUBDIVISIONS = [1, 2, 5];

/**
 * Round decade (and, on a short frame, 1-2-5) values strictly inside `[min, max]` — or inside
 * `[1, max]` when `min` is 0, which is what a `log1p` frame looks like.
 *
 * `maxTicks` thins by taking every Nth decade rather than dropping the top ones, so the ladder stays a
 * ladder — evenly spaced on screen — instead of ending abruptly.
 */
export function logTickValues(min: number, max: number, maxTicks: number): number[] {
  if (!(min >= 0) || !(max > min) || !(maxTicks > 0)) return [];
  // A `log1p` frame STARTS at zero — that is the whole reason the backend picks `log1p` over `log` —
  // and `log10(0)` is `-Infinity`, so requiring `min > 0` returned NO interior ticks for every one of
  // those axes: the exact charts that most need a magnitude ladder were drawn with only their two ends
  // labelled. Below 1, `log1p` is near-linear and each decade occupies a sliver of the track (0.1 sits
  // at ~1% of a frame reaching 5,000), so a tick there would be unreadable clutter rather than a
  // landmark. 1 is where the axis starts behaving logarithmically, and it is the honest floor.
  const lo = min > 0 ? min : 1;
  if (!(max > lo)) return [];
  const firstExp = Math.ceil(Math.log10(lo));
  const lastExp = Math.floor(Math.log10(max));
  const decades: number[] = [];
  for (let e = firstExp; e <= lastExp; e += 1) decades.push(Math.pow(10, e));

  // A short frame (under two decades) has too few decade ticks to read as a scale at all — a 100..900
  // column would get exactly one. Subdividing gives it a ladder without leaving the round numbers.
  if (decades.length < 3) {
    const fine: number[] = [];
    for (let e = firstExp - 1; e <= lastExp + 1; e += 1) {
      for (const s of SUBDIVISIONS) {
        const v = s * Math.pow(10, e);
        if (v >= lo && v <= max) fine.push(v);
      }
    }
    // THIN BY STRIDING, exactly as the decade branch below does and as this function's docblock
    // promises — `fine` is built in ascending order, so `slice(0, maxTicks)` kept the LOWEST candidates
    // and dropped the highest. `logTickValues(0.11, 99, 6)` returned `[0.2, 0.5, 1, 2, 5, 10]`: a
    // perfectly good 20 and 50 discarded, and the top third of the axis left with no interior tick —
    // the crowded-at-one-end look a log axis exists to prevent. (The trailing `.sort()` this replaces
    // was redundant: `fine` was already ascending, which is a tell that the slice was expected to
    // reorder something.)
    if (fine.length <= maxTicks) return fine;
    const fineStride = Math.ceil(fine.length / maxTicks);
    return fine.filter((_, i) => i % fineStride === 0);
  }

  if (decades.length <= maxTicks) return decades;
  const stride = Math.ceil(decades.length / maxTicks);
  return decades.filter((_, i) => i % stride === 0);
}
